import type {
  ItilConsolidatedField,
  ItilConsolidatedSection,
  ItilTranslationStatus,
  PdfItilFieldSelection
} from './types';
import {
  ITIL_CLOSE_MARKER,
  ITIL_COMMENT_MARKER_REGEX,
  ITIL_SECTION_DELIMITER
} from './constants';

const LEGACY_CLOSE_ITIL_REGEX = /Traducción ITIL \(Cierre\):\s*([\s\S]*)$/;

export function buildConsolidatedRawDescription(
  fields: ItilConsolidatedField[]
): string {
  const filled = fields.filter((f) => f.value?.trim());
  if (!filled.length) {
    return '';
  }
  return filled.map((f) => `## ${f.label}\n${f.value.trim()}`).join('\n');
}

export function parseConsolidatedTranslation(
  text: string,
  fieldLabels: string[]
): ItilConsolidatedSection[] {
  if (!text?.trim()) {
    return [];
  }

  const trimmed = text.trim();

  if (!trimmed.includes('##')) {
    return [{ label: '', text: trimmed }];
  }

  const sections: ItilConsolidatedSection[] = [];
  const parts = trimmed.split(ITIL_SECTION_DELIMITER).filter(Boolean);

  parts.forEach((part) => {
    const newlineIdx = part.indexOf('\n');
    if (newlineIdx === -1) {
      sections.push({ label: part.trim(), text: '' });
      return;
    }
    const label = part
      .slice(0, newlineIdx)
      .replace(/^##\s*/, '')
      .trim();
    const body = part.slice(newlineIdx + 1).trim();
    sections.push({ label, text: body });
  });

  if (!sections.length && fieldLabels.length) {
    return [{ label: '', text: trimmed }];
  }

  return sections;
}

export function canSaveItilGate(
  statuses: ItilTranslationStatus[],
  options?: { allowSkipped?: boolean }
): boolean {
  const allowSkipped = options?.allowSkipped ?? true;
  return statuses.every(
    (s) =>
      s === 'ready' ||
      (allowSkipped && (s === 'skipped' || s === 'storedAsOriginal'))
  );
}

export function isItilGateBlocked(status: ItilTranslationStatus): boolean {
  return !['ready', 'skipped', 'storedAsOriginal'].includes(status);
}

export function resolveItilTranslatedPayload(
  status: ItilTranslationStatus,
  translatedText: string
): string | undefined {
  if (status === 'error' || status === 'storedAsOriginal') {
    return '';
  }
  if (status === 'ready' && translatedText.trim()) {
    return translatedText.trim();
  }
  return undefined;
}

export function countPendingItilFields(
  statuses: ItilTranslationStatus[]
): number {
  return statuses.filter((s) => isItilGateBlocked(s)).length;
}

export function buildPdfFieldSelections(
  fields: PdfItilFieldSelection[]
): Record<string, 'original' | 'itil'> {
  const result: Record<string, 'original' | 'itil'> = {};
  fields.forEach((f) => {
    result[f.key] = f.itilText ? 'itil' : 'original';
  });
  return result;
}

export function getCloseItilText(
  commentDetails?: string | null,
  itilTranslatedCloseComment?: string | null
): string | null {
  if (itilTranslatedCloseComment?.trim()) {
    return itilTranslatedCloseComment.trim();
  }
  const match = commentDetails?.match(LEGACY_CLOSE_ITIL_REGEX);
  return match?.[1]?.trim() ?? '';
}

export function stripLegacyCloseItilFromCommentDetails(
  commentDetails?: string
): string {
  if (!commentDetails?.trim()) {
    return '';
  }
  return commentDetails.replace(LEGACY_CLOSE_ITIL_REGEX, '').trimEnd();
}

export function stripItilTranslationFromComment(comment: string): string {
  if (!comment?.trim()) {
    return '';
  }
  const match = ITIL_COMMENT_MARKER_REGEX.exec(comment);
  if (match?.index !== undefined) {
    return comment.slice(0, match.index).trimEnd();
  }
  return comment.trim();
}

export function splitCommentWithItilCloseBlock(comment: string): {
  original: string;
  itilText: string | null;
} {
  if (!comment?.trim()) {
    return { original: '', itilText: null };
  }

  const markerIndex = comment.indexOf(ITIL_CLOSE_MARKER);
  if (markerIndex === -1) {
    return { original: comment.trim(), itilText: null };
  }

  const original = comment.slice(0, markerIndex).trimEnd();
  const afterMarker = comment
    .slice(markerIndex + ITIL_CLOSE_MARKER.length)
    .trim();
  return {
    original,
    itilText: afterMarker || null
  };
}

export function formatCloseCommentsText(commentDetails?: string): string {
  const stripped = stripLegacyCloseItilFromCommentDetails(commentDetails);
  if (!stripped?.trim()) {
    return '';
  }
  return stripped
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .map((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      return match ? `${match[1]}: ${match[2]}` : line;
    })
    .join('\n');
}

export function parseClosingFieldsFromCommentDetails(
  commentDetails?: string
): { fieldName: string; text: string }[] {
  const stripped = stripLegacyCloseItilFromCommentDetails(commentDetails);
  if (!stripped?.trim()) {
    return [];
  }

  return stripped
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .map((line) => {
      const parts = line.split(/:\s(.+)/);
      if (parts.length >= 2 && parts[0] && parts[1] !== undefined) {
        return { fieldName: parts[0].trim(), text: parts[1] };
      }
      return null;
    })
    .filter(
      (item): item is { fieldName: string; text: string } => item !== null
    );
}

type ClosingFieldItilFlag = {
  translateToItilGenius?: boolean;
  /** @deprecated use translateToItilGenius */
  exportToItil?: boolean;
};

export function isClosingFieldItilExport(field: ClosingFieldItilFlag): boolean {
  if (field.translateToItilGenius === false) {
    return false;
  }
  if (field.exportToItil === false) {
    return false;
  }
  return true;
}
